import type { ComponentDocMap } from './types'

/**
 * Prop documentation for the `dataset_table` component.
 * Keep in sync with DatasetTableProps in src/ui/uiConfig/components/DatasetTable/DatasetTable.types.ts
 */
export const datasetTablePropDocs: ComponentDocMap = {
  items: {
    description:
      'Array of columns to display. Each item maps to a dataset via data_id === return_id. The table_order field controls column order.',
    type: 'DatasetTableItem[]',
    example: '[{ title: "__Heat load", data_id: "heatload", unit: "MW", decimals: 1, table_order: 0 }]',
  },
  'items[].title': {
    description: 'Column header. Prefix with __ for i18n translation.',
    type: 'string',
    example: '"__Production"',
  },
  'items[].sub_title': {
    description: 'Secondary label shown below the main column header.',
    type: 'string',
    example: '"MWh"',
  },
  'items[].table_order': {
    description: 'Sort order for columns. Lower values appear first.',
    type: 'number',
    example: '0',
  },
  'items[].unit': {
    description: 'Unit shown for this column values.',
    type: 'string',
    example: '"MW"',
  },
  'items[].decimals': {
    description: 'Decimal precision for values in this column.',
    type: 'number',
    example: '1',
  },
  'items[].data_type': {
    description: 'Formatting hint for this column values.',
    type: 'string',
    example: '"energy"',
  },
  'items[].border_right': {
    description: 'Adds a visual divider on the right side of this column.',
    type: 'boolean',
    example: 'true',
  },
  'items[].show_only_in_export': {
    description: 'Hides the column in the rendered table but includes it in Excel exports.',
    type: 'boolean',
    example: 'true',
  },
  title: {
    description: 'Optional heading shown above the table.',
    type: 'string',
    example: '"__Daily summary"',
  },
  do_not_show_zero_or_null_values_in_table: {
    description: 'Excludes datasets whose values are all zero or null from the table.',
    type: 'boolean',
    example: 'true',
  },
  do_not_show_download_button: {
    description: 'Hides the CSV/Excel download button.',
    type: 'boolean',
    example: 'true',
  },
  do_not_show_sum_or_average_row: {
    description: 'Hides the sum/average footer row.',
    type: 'boolean',
    example: 'true',
  },
  do_not_show_min_max_row: {
    description: 'Hides the min/max footer row.',
    type: 'boolean',
    example: 'true',
  },
  do_not_show_top_toolbar: {
    description: 'Hides the search/filter toolbar at the top of the table.',
    type: 'boolean',
    example: 'true',
  },
  show_only_value_in_future: {
    description: 'Grays out or hides rows for past timestamps.',
    type: 'boolean',
    example: 'true',
  },
  initial_page_size: {
    description: 'Number of rows per page on initial render.',
    type: 'number',
    example: '24',
  },
  initial_page_index: {
    description: 'Initial page index on first render.',
    type: 'number',
    example: '0',
  },
  show_initial_page_with_current_optimization_hour: {
    description: 'Starts table on the page containing the current optimization hour.',
    type: 'boolean',
    example: 'true',
  },
  show_initial_page_with_current_day: {
    description: 'Starts table on the page containing current day values.',
    type: 'boolean',
    example: 'true',
  },
  customColumns: {
    description: 'Custom MRT column definitions merged with generated columns.',
    type: 'TimeSeriesDataTableColumnDef[]',
  },
  columnOverrides: {
    description: 'Per data_id column override map.',
    type: 'Record<string, TimeSeriesDataTableColumnDef>',
  },
  enableEditing: {
    description: 'Enables editable cells where supported.',
    type: 'boolean',
  },
  enableColumnActions: {
    description: 'Enables column actions menu.',
    type: 'boolean',
  },
  tableKey: {
    description: 'Stable table key used for state persistence.',
    type: 'string',
  },
  disableCustomSorting: {
    description: 'Disables custom sorting behavior and uses default sort.',
    type: 'boolean',
  },
  sorting_function: {
    description:
      'Controls how columns are ordered. "table_order" uses the items[].table_order field, "chart_items" matches chart item order.',
    type: '"table_order" | "order" | "chart_items" | "alphabetical" | "chart_table_order"',
    example: '"table_order"',
  },
  use_column_color_from_items: {
    description: 'Colors every column alternately using item colors instead of the default grey-stripe pattern.',
    type: 'boolean',
    example: 'true',
  },
  footer_calculates_per_page: {
    description:
      'Calculates footer aggregates (sum, average, min, max) for the current page only rather than the full dataset.',
    type: 'boolean',
    example: 'true',
  },
  max_height: {
    description: 'Maximum CSS height of the table container before scrolling.',
    type: 'string | number',
    example: '"600px"',
  },
  sticky_table_header: {
    description: 'Pins the column header row when scrolling vertically.',
    type: 'boolean',
    example: 'true',
  },
  expandableHours: {
    description: 'Allows rows to be expanded to show sub-hourly data.',
    type: 'boolean',
    example: 'true',
  },
  hideSingleValueDatasets: {
    description: 'Hides columns/datasets that only contain a single value.',
    type: 'boolean',
    example: 'true',
  },
  enableCellRangeSelectionForClipboard: {
    description:
      'Explicit opt-in for rectangular cell range selection and clipboard copy. Defaults to false and must be set to true per dataset_table config.',
    type: 'boolean',
    example: 'true',
  },
  showCloseButton: {
    description: 'Shows a close button instead of fullscreen control.',
    type: 'boolean',
  },
  onCloseClick: {
    description: 'Callback triggered when the close button is clicked.',
    type: '() => void',
  },
  fillParentHeight: {
    description: 'Expands the table container to fill parent height.',
    type: 'boolean',
  },
}
